import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BookingCard } from '../components/BookingCard';
import { useSeo } from '../lib/useSeo';
import { servicesData } from '../data/services';
import { ArrowRight } from 'lucide-react';
export function MeditationBetweenWorlds() {
  const service = servicesData.find(s => s.id === 'meditation-between-worlds')!;
  const otherServices = servicesData.filter(s => s.id !== service.id);
  useSeo({
    title: service.title,
    description: service.summary,
    path: service.link,
    image: service.image
  });
  return <div className="min-h-screen flex flex-col bg-cream" data-id="element-377">
      <Header data-id="element-378" />

      {/* Hero Section */}
      <section className="relative h-[65vh] min-h-[480px] flex items-center justify-center overflow-hidden" data-id="element-379">
        <div className="absolute inset-0 z-0" data-id="element-380">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-381" />
          <div className="absolute inset-0 bg-charcoal/40" data-id="element-382"></div>
        </div>

        <div className="relative z-10 text-center px-6 mt-16" data-id="element-383">
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-4 drop-shadow-md" data-id="element-384">
            {service.title}
          </h1>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none" data-id="element-385">
          <WavyDivider fill="#F8F2E4" data-id="element-386" />
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-6" data-id="element-387">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-388">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-6" data-id="element-389">
            <p className="text-xl font-light text-charcoal-50 leading-relaxed italic" data-id="element-390">
              Experience deep transformation through guided meditation. This is
              not just sitting quietly; it's an active journey within. Each
              session starts with an introduction, preparing you to face
              whatever arises, while embracing your bodily experiences with
              awareness. You'll dive into a profound inner shift and find a
              fresh perspective on your life.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-391">
              Through "Meditation Between Worlds," you won't escape your
              day-to-day, but rather confront it head-on. This practice is
              multi-dimensional; it engages your physical, mental, and energetic
              self. You'll learn to dismantle old habits and thought patterns
              that dictate your reality. With your eyes closed, focus inward,
              and let the exploration of your body and mind guide you. Analyze
              your discomforts, beliefs, and any limitations — transforming them
              into opportunities for growth. Regular sessions help you reconnect
              with your authentic self and unveil new inner paths.
            </p>

            <p className="text-charcoal-50 font-light leading-relaxed" data-id="element-392">
              As you routinely practice, you'll gain tools to navigate both
              mental and physical resistance with poise. Remember, stillness
              marks the start of a transformative journey; as time passes,
              you'll find yourself becoming clearer, more grounded, and truly
              alive, embracing the core of who you are beyond past narratives.
            </p>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-393">
            <BookingCard price={service.price} priceDetail={service.priceDetail} duration="Approx. 60 minutes" format="In-person or Online" data-id="element-394" />
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="bg-rose-50 py-20 px-6 relative mt-12" data-id="element-395">
        <div className="absolute top-0 left-0 right-0 -translate-y-full pointer-events-none" data-id="element-396">
          <WavyDivider fill="#F5EBEB" position="top" data-id="element-397" />
        </div>

        <div className="max-w-7xl mx-auto" data-id="element-398">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-12 text-center" data-id="element-399">
            You might also like
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-400">
            {otherServices.map(s => <Link key={s.id} to={s.link} className="group block bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-md transition-all" data-id="element-401">
                <div className="aspect-[4/3] overflow-hidden" data-id="element-402">
                  <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-403" />
                </div>
                <div className="p-6" data-id="element-404">
                  <h3 className="font-serif text-xl text-charcoal mb-2" data-id="element-405">
                    {s.title}
                  </h3>
                  <span className="text-sage-700 text-sm flex items-center group-hover:text-sage-600 transition-colors" data-id="element-406">
                    Explore <ArrowRight size={14} className="ml-1" data-id="element-407" />
                  </span>
                </div>
              </Link>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-408" />
    </div>;
}